import classnames from 'classnames'
import { useEffect, useRef } from 'react'
import { getCurrentLanguage } from '../../platform'
import { findActiveCueIndex, getTranscriptScrollTarget } from '../functions'
import { useTimecodedCues } from '../useTimecodedCues'
import { Cue } from '../webvtt'

interface Props {
	transcription?: string
	transcriptionFormat?: 'html' | 'timecoded'
	mediaItemId?: number
	currentTime: number
	isPlaying?: boolean
	variant: 'card' | 'modal'
	className?: string
}

const SyncedTranscript = ({
	transcription,
	transcriptionFormat,
	mediaItemId,
	currentTime,
	isPlaying,
	variant,
	className,
}: Props) => {
	const { cues, loading } = useTimecodedCues(mediaItemId, transcriptionFormat, getCurrentLanguage())
	const isTimecoded = cues.length > 0
	const activeIndex = isTimecoded ? findActiveCueIndex(cues, currentTime) : -1

	// Render the timecoded scroll view as soon as we know this is a timecoded track
	// — including the brief window where its cues are still loading (`loading` is
	// only ever true for a timecoded track). Otherwise the static HTML transcript
	// renders first and visibly swaps to the cue list once the fetch resolves. We
	// still fall back to the static branch when a timecoded fetch finishes with no
	// cues (404 / error / empty), which is the intended graceful fallback.
	const showTimecoded = isTimecoded || loading

	const scrollRoot = useRef<HTMLDivElement | null>(null)
	const segmentRefs = useRef<(HTMLElement | null)[]>([])

	// Values read inside the rAF loop are kept in refs so the loop never sees a
	// stale closure and we never have to restart it on every render.
	const cuesRef = useRef<Cue[]>(cues)
	const propsRef = useRef<{ currentTime: number; isPlaying: boolean }>({
		currentTime,
		isPlaying: !!isPlaying,
	})
	const lastTimeRef = useRef(currentTime)
	const lastAtRef = useRef(performance.now())
	const isInteractingRef = useRef(false)
	const interactTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// rAF loop bookkeeping: whether the loop is currently scheduled, and the id
	// of the pending frame so we can cancel it.
	const runningRef = useRef(false)
	const frameRef = useRef(0)

	// Refresh refs every render so the loop reads current values.
	cuesRef.current = cues
	propsRef.current = { currentTime, isPlaying: !!isPlaying }

	// Re-anchor the extrapolation clock whenever currentTime ticks.
	useEffect(() => {
		lastTimeRef.current = currentTime
		lastAtRef.current = performance.now()
	}, [currentTime])

	// Smooth auto-scroll loop, only meaningful when timecoded. The loop idles
	// (stops rescheduling) once playback is paused and the scroll has settled, so
	// we don't wake the main thread every frame for the component's whole life.
	const startLoop = () => {
		if (runningRef.current) return
		runningRef.current = true
		frameRef.current = requestAnimationFrame(tick)
	}

	const tick = () => {
		const root = scrollRoot.current
		const activeCues = cuesRef.current
		const { currentTime: ct, isPlaying: playing } = propsRef.current

		// While the user is mid-interaction we suppress the scroll write but keep
		// the loop alive so it resumes auto-scroll after the interaction window.
		const interacting = isInteractingRef.current

		let target = 0
		if (root && activeCues.length && !interacting) {
			const t = playing ? lastTimeRef.current + (performance.now() - lastAtRef.current) / 1000 : ct

			// Each segment's centre as an offset within the scroll content. We use
			// getBoundingClientRect relative to the scroll root (rather than offsetTop)
			// so the measurement is correct regardless of the offsetParent — offsetTop
			// is relative to the nearest positioned ancestor, which is NOT the scroll
			// root here, so it would yield inflated offsets and scroll to the wrong place.
			// Bound to the current cue count so stale trailing refs (from a shrunk cue
			// list) are ignored without a separate reset effect.
			const rootTop = root.getBoundingClientRect().top
			const centers = activeCues.map((_, i) => {
				const el = segmentRefs.current[i]
				if (!el) return 0
				const rect = el.getBoundingClientRect()
				return rect.top - rootTop + root.scrollTop + rect.height / 2
			})
			target = getTranscriptScrollTarget(centers, activeCues, t, root.clientHeight)
			target = Math.min(target, Math.max(0, root.scrollHeight - root.clientHeight))

			const delta = target - root.scrollTop
			if (Math.abs(delta) > 0.5) root.scrollTop += delta * 0.18
		}

		// Continue while playing or interacting, or until the scroll settles.
		if (playing || interacting) {
			frameRef.current = requestAnimationFrame(tick)
		} else if (root && Math.abs(target - root.scrollTop) > 0.5) {
			frameRef.current = requestAnimationFrame(tick)
		} else {
			runningRef.current = false
		}
	}

	// Start the loop when timecoded; tear it down on unmount / when not timecoded.
	useEffect(() => {
		if (isTimecoded) startLoop()
		return () => {
			cancelAnimationFrame(frameRef.current)
			runningRef.current = false
			if (interactTimeoutRef.current) clearTimeout(interactTimeoutRef.current)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTimecoded])

	// Re-arm the loop on play or on a seek-while-paused so motion resumes and
	// settles to the new position. startLoop() is a cheap no-op when running.
	useEffect(() => {
		if (isTimecoded) startLoop()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTime, isPlaying, isTimecoded])

	const onInteract = () => {
		isInteractingRef.current = true
		if (interactTimeoutRef.current) clearTimeout(interactTimeoutRef.current)
		interactTimeoutRef.current = setTimeout(() => {
			isInteractingRef.current = false
		}, 4000)
	}

	if (!showTimecoded) {
		return (
			<div className={classnames('synced-transcript', variant, className)}>
				<div dangerouslySetInnerHTML={{ __html: transcription || '' }} />
			</div>
		)
	}

	return (
		<div
			ref={scrollRoot}
			className={classnames('synced-transcript', variant, isPlaying && 'playing', className)}
			onWheel={onInteract}
			onTouchStart={onInteract}
			onTouchMove={onInteract}
		>
			{cues.map((c, i) => (
				<p
					key={i}
					ref={el => {
						segmentRefs.current[i] = el
					}}
					className={classnames('synced-transcript-segment', i === activeIndex && 'active')}
				>
					{c.text}
				</p>
			))}
		</div>
	)
}

export default SyncedTranscript
